import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Building2, Plus, RefreshCw, TriangleAlert } from "lucide-react";
import HelpdeskSuiteLayout from "../../../layouts/helpdeskLayout/HelpdeskSuiteLayout";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Card } from "../../../components/ui/Card";
import { Button } from "../../../components/ui/Button";
import { Skeleton } from "../../../components/ui/Skeleton";
import { EmptyState } from "../../../components/ui/EmptyState";
import { PendingBackendNotice } from "../../../components/ui/PendingBackendNotice";
import useInternalChat from "./internal/useInternalChat";
import InternalChatThread from "./internal/InternalChatThread";
import InternalComposer from "./internal/InternalComposer";

// Canal "Interno" — asistente de gerencia.
//
// Es el hilo PROPIO del usuario logueado con el agente IA de audiencia
// `internal` del tenant: no hay bandeja, no hay contacto y no se asigna a nadie.
// Vive en su propia ruta (/admin/helpdesk/interno) porque no es un caso de
// atención al cliente, sino una consulta interna.
//
// Tres degradaciones explícitas, todas navegables:
//   - 404/405 → PendingBackendNotice (la ruta aún no está desplegada).
//   - 409 NO_INTERNAL_AGENT → EmptyState con CTA al editor de agentes.
//   - error real → Card con reintento.

function LoadingState() {
  return (
    <div className="flex flex-col gap-3 px-4 sm:px-6 py-4" aria-hidden="true">
      <Skeleton className="h-10 w-2/3 rounded-2xl" />
      <Skeleton className="h-14 w-3/4 rounded-2xl self-end" />
      <Skeleton className="h-10 w-1/2 rounded-2xl" />
    </div>
  );
}

export default function InternalAssistantPage() {
  const { t } = useTranslation();
  const {
    status,
    agent,
    messages,
    draft,
    setDraft,
    sending,
    awaiting,
    send,
    retry,
    maxLength,
  } = useInternalChat();

  const header = (
    <div className="shrink-0 px-4 sm:px-6 pt-5 pb-4 border-b border-border bg-bg">
      <PageHeader
        icon={
          <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
            <Building2 className="w-5 h-5" aria-hidden="true" />
          </span>
        }
        title={t("Asistente interno")}
        subtitle={t(
          "Tu canal privado con la IA del equipo interno. No abre un caso ni lo ve el cliente.",
        )}
      />
    </div>
  );

  const body = (() => {
    if (status === "loading") return <LoadingState />;

    if (status === "missing") {
      return (
        <div className="px-4 sm:px-6 py-5">
          <PendingBackendNotice>
            {t(
              "El canal interno todavía no está disponible: el servicio aún no expone esta ruta.",
            )}
          </PendingBackendNotice>
        </div>
      );
    }

    if (status === "noAgent") {
      return (
        <EmptyState
          icon={<Building2 className="w-6 h-6" />}
          title={t("No hay un asistente de equipo interno")}
          description={t(
            "Crea un agente con audiencia Equipo interno / gerencia para poder conversar por acá.",
          )}
          action={
            // Link estilado como botón primario: ui/Button no acepta `as`, y
            // envolverlo en un <a> anidaría dos elementos interactivos.
            <Link
              // audience=internal: el editor abre el alta con la audiencia ya
              // elegida — es la que este canal necesita, y volver a pedirla a
              // mano es justo donde el usuario se equivoca.
              to="/admin/helpdesk/ai?agent=new&audience=internal"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-sm font-medium bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
            >
              <Plus className="w-4 h-4" aria-hidden="true" />
              {t("Crear agente interno")}
            </Link>
          }
        />
      );
    }

    if (status === "error") {
      return (
        <div className="px-4 sm:px-6 py-5">
          <Card className="max-w-md mx-auto text-center">
            <span
              className="mx-auto mb-3 inline-flex w-10 h-10 items-center justify-center rounded-full bg-status-warning-soft text-status-warning"
              aria-hidden="true"
            >
              <TriangleAlert className="w-5 h-5" />
            </span>
            <p className="text-sm text-fg-muted">
              {t("No se pudo cargar la conversación interna.")}
            </p>
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={retry}
                iconLeft={<RefreshCw className="w-4 h-4" aria-hidden="true" />}
              >
                {t("Reintentar")}
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    return (
      <>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <InternalChatThread
            messages={messages}
            awaiting={awaiting}
            agentName={agent?.name || null}
          />
        </div>
        <InternalComposer
          value={draft}
          onChange={setDraft}
          onSend={send}
          sending={sending}
          awaiting={awaiting}
          maxLength={maxLength}
        />
      </>
    );
  })();

  return (
    <HelpdeskSuiteLayout>
      <div className="flex flex-col h-full min-h-0 bg-bg">
        {header}
        {body}
      </div>
    </HelpdeskSuiteLayout>
  );
}
